<!--
  Ligands tab, ported from vendor-ref/mara/app/ui/src/components/nanome/WorkspaceLigands.vue: the empty state with
  'Sketch new', the table, and the fullscreen Dialog. Rows come from the loaded models (src/ligands/ligands.ts)
  rather than the server's ligand list; sketching needs Ketcher and the server, so 'Sketch new' opens the nudge.
-->
<script setup lang="ts">
import { computed, ref } from 'vue';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Popover from 'primevue/popover';
import { useLiteStore } from '@nanome-lite/state';
import { deriveLigands } from '@/ligands/ligands';
import EmptyResult from '../EmptyResult.vue';
import TeaserCta from '../TeaserCta.vue';
import WorkspaceLigandsTable from './WorkspaceLigandsTable.vue';

const store = useLiteStore();
const isFullscreen = ref(false);
const sketchPopover = ref<InstanceType<typeof Popover>>();

const ligands = computed(() => deriveLigands(store.entries, store.models));

function sketch(e: Event) {
  sketchPopover.value?.toggle(e);
}
</script>

<template>
  <EmptyResult v-if="ligands.length === 0" item="ligands" icon="i-tabler-hexagon" data-test="ligands-empty">
    <Button
      icon="i-tabler-hexagon-plus-2"
      label="Sketch new"
      severity="secondary"
      size="small"
      rounded
      data-test="ligands-sketch"
      @click="sketch"
    />
  </EmptyResult>

  <WorkspaceLigandsTable
    v-else
    :ligands="ligands"
    @fullscreen="isFullscreen = true"
    @sketch="sketch"
  />

  <Dialog
    v-model:visible="isFullscreen"
    pt:content="h-full min-h-0 flex flex-col gap-4"
    class="w-full h-full max-w-90%"
    :draggable="false"
    header="Ligands"
    dismissable-mask
    modal
  >
    <WorkspaceLigandsTable :ligands="ligands" is-fullscreen @sketch="sketch" />
  </Dialog>

  <Popover ref="sketchPopover">
    <TeaserCta title="Sketch ligands in Nanome" text="Draw a new molecule or a variant, align it to the pocket, and dock it." />
  </Popover>
</template>
